/**
 * Journal tags: list with counts and entries filtered by tag.
 */
import { createRoute, useSearch } from "@tanstack/solid-router";
import { Link, useNavigate } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { PenLine, Tag as TagIcon, Trash2 } from "@fleet/icons";
import { cn } from "@journal/shared";
import { useAllEntryTagsQuery, useEntriesByTagQuery, useTagsQuery } from "@journal/shared/solid";
import { appRoute } from "./route";
import { PageContainer } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { TagIllustration } from "@/components/ui/illustrations";
import { buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { EntryCard } from "@/features/journal/components/entry-card";
import { useJournalData } from "@/features/journal/hooks/use-journal-data";
import { useEntryMutations } from "@/features/journal/hooks/use-entry-mutations";

function TagsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { db, drizzle, userId, staleTime } = useJournalData();
  const { deleteTag } = useEntryMutations();

  // The selected tag lives in the URL (?tag=) so it is shareable and
  // reconstructible on reload (idempotent). The URL is the source of truth.
  const search = useSearch({ from: "/_app/tags" });
  const [selectedTagId, setSelectedTagId] = createSignal<string | undefined>(
    search().tag ?? undefined,
  );
  const [tagToDelete, setTagToDelete] = createSignal<string | null>(null);

  // URL → signal: reflects back/forward and pasted links.
  createEffect(() => {
    setSelectedTagId(search().tag ?? undefined);
  });

  // signal → URL: toggles the selected tag in the query params.
  const selectTag = (id: string) => {
    const next = selectedTagId() === id ? undefined : id;
    void navigate({ to: "/tags", search: next === undefined ? {} : { tag: next }, replace: true });
  };

  const tags = useTagsQuery({ db, drizzle, userId, staleTime });
  const allEntryTags = useAllEntryTagsQuery({ db, drizzle, userId, staleTime });
  const entries = useEntriesByTagQuery({ db, drizzle, userId, staleTime, tagId: selectedTagId });

  const countsByTag = createMemo(() => {
    const map = new Map<string, number>();
    for (const row of allEntryTags.data ?? []) {
      map.set(row.tag_id, (map.get(row.tag_id) ?? 0) + 1);
    }
    return map;
  });

  const selectedTag = createMemo(() => tags.data?.find((tag) => tag.id === selectedTagId()));

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-4 pb-16">
      <h1 class="pt-2 text-xl font-bold text-foreground">{t(msg`Tags`)}</h1>

      <Show
        when={(tags.data ?? []).length > 0}
        fallback={
          <Show when={!tags.isLoading}>
            <EmptyState
              icon={TagIcon}
              illustration={<TagIllustration />}
              title={t(msg`No tags yet`)}
              description={t(msg`Create tags from the composer to organize your entries.`)}
              onIconActivate={() => {
                void navigate({ to: "/" });
              }}
              iconActionLabel={t(msg`Write an entry`)}
              action={
                <Link to="/" class={buttonVariants({ size: "sm" })}>
                  <PenLine class="h-4 w-4" />
                  {t(msg`Write an entry`)}
                </Link>
              }
            />
          </Show>
        }
      >
        <div class="flex flex-wrap gap-2" data-testid="tags-list">
          <For each={tags.data ?? []}>
            {(tag) => (
              <div class="group relative">
                <button
                  type="button"
                  data-testid={`tag-chip-${tag.name ?? ""}`}
                  class={cn(
                    "rounded-full border border-border/40 bg-card/60 px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground",
                    selectedTagId() === tag.id && "border-primary/40 bg-primary/12 text-primary",
                  )}
                  onClick={() => {
                    selectTag(tag.id);
                  }}
                >
                  #{tag.name}
                  <span class="ml-1.5 text-[11px] opacity-60">
                    {countsByTag().get(tag.id) ?? 0}
                  </span>
                </button>
                <button
                  type="button"
                  class="absolute -right-1.5 -top-1.5 hidden h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow group-hover:flex"
                  aria-label={t(msg`Delete tag`)}
                  onClick={() => setTagToDelete(tag.id)}
                >
                  <Trash2 class="h-3 w-3" />
                </button>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={selectedTag()}>
        {(tag) => (
          <section class="flex flex-col gap-3" data-testid="tag-entries">
            <h2 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t(msg`Entries tagged`)} #{tag().name ?? ""}
            </h2>
            <For each={entries.data ?? []}>{(entry) => <EntryCard entry={entry} showDate />}</For>
          </section>
        )}
      </Show>

      <ConfirmDialog
        title={t(msg`Delete this tag?`)}
        description={t(msg`The tag will be removed from all entries. Entries are kept.`)}
        variant="destructive"
        open={tagToDelete() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setTagToDelete(null);
          }
        }}
        onConfirm={() => {
          const id = tagToDelete();
          if (id) {
            if (selectedTagId() === id) {
              void navigate({ to: "/tags", search: {}, replace: true });
            }
            void deleteTag(id);
          }
          setTagToDelete(null);
        }}
      />
    </PageContainer>
  );
}

interface TagsSearch {
  tag?: string;
}

export const tagsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tags",
  validateSearch: (raw: Record<string, unknown>): TagsSearch => ({
    tag: typeof raw.tag === "string" && raw.tag !== "" ? raw.tag : undefined,
  }),
  component: TagsPage,
});
